import { useState } from "react";
import Accordion from "components/core/basic/Accordion";
import Card from "components/core/basic/Card";
import { Flex } from "../../../../styled-system/jsx";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import api from "utils/api";
import { successToast, errorToast } from "utils/toasts";
import { ORG_STATUS_OPTIONS } from "views/ApplicationsMenu";
import { Button } from "components/core/basic/Button";
import CenteredModal from "components/core/basic/CenteredModal";
import AmazonProfilesTable from "../onboarding/AmazonProfilesTable";
import { useFetch } from "hooks/api";
import { useAppSelector } from "redux/hooks";
import { css } from "../../../../styled-system/css";
import FormGroupTile from "components/core/basic/FormGroupTile";

const OrgStatusSection = () => {
  const [showModal, setShowModal] = useState(false);
  const [updatedOrgStatus, setUpdatedOrgStatus] = useState({ label: "No Update", value: null });
  const [selectedOrgs, setSelectedOrgs] = useState([]);

  const user = useAppSelector((state) => state.user);
  const queryClient = useQueryClient();

  const { data: trellisOrgsData, isLoading: isTrellisOrgsDataLoading } = useFetch(
    [user.organization_id, "trellisOrgs"],
    "/api/organizations/",
    { page_size: 5000 },
    {
      select: (d) => d.data,
      default: [],
    },
  );

  const updateStatusHandler = useMutation({
    mutationFn: async () => {
      if (selectedOrgs?.length === trellisOrgsData?.length) {
        const body = {
          status: updatedOrgStatus.value,
          all_orgs: true,
        };
        return await api.put(`/api/application/orgstatus/`, body);
      }

      const orgStatusMutations = [];

      selectedOrgs?.forEach((orgId) => {
        const body = {
          target_org: orgId,
          status: updatedOrgStatus.value,
          all_orgs: false,
        };

        orgStatusMutations.push(api.put(`/api/application/orgstatus/`, body));
      });

      const mutationResults = await Promise.allSettled(orgStatusMutations);
      const errors = mutationResults.filter((result) => result.status === "rejected");

      if (errors.length) {
        throw new Error("Error while updating organization status.");
      }

      return true;
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries();
      successToast({
        message: "Organization Status Successfully Updated",
      });
      reset();

      setTimeout(() => {
        window.location.reload();
      }, 1500);
    },

    onError: (error) => {
      errorToast({
        message: "Failed to Update Organization Status",
        error,
      });
    },
  });

  const reset = () => {
    setSelectedOrgs([]);
    setUpdatedOrgStatus({ label: "No Update", value: null });
    setShowModal(false);
  };

  const orgStatusOptions = [
    { label: "No Update", value: null },
    { label: ORG_STATUS_OPTIONS.ACTIVE.label, value: ORG_STATUS_OPTIONS.ACTIVE.value },
    { label: ORG_STATUS_OPTIONS.PRE_TRIAL.label, value: ORG_STATUS_OPTIONS.PRE_TRIAL.value },
    { label: ORG_STATUS_OPTIONS.TRIAL_ACTIVE.label, value: ORG_STATUS_OPTIONS.TRIAL_ACTIVE.value },
    { label: ORG_STATUS_OPTIONS.DISCONNECTED.label, value: ORG_STATUS_OPTIONS.DISCONNECTED.value },
  ];

  return (
    <Accordion title={"Organization Status"}>
      <Card>
        <Flex gap="small" justifyContent="center" className={css({ width: "100%", padding: "large" })}>
          <Button onPress={() => setShowModal(true)}>Manage Organization Status</Button>
        </Flex>
        <CenteredModal
          show={showModal}
          closeModal={() => {
            reset();
          }}
          title={"Update Organization Status"}
          content={
            <Flex direction="column">
              <div className={css({ maxWidth: "500px", paddingTop: "large", paddingBottom: "large" })}>
                <FormGroupTile
                  label="New Organization Status"
                  type="select"
                  stateName="orgStatus"
                  handleChildFormElement={(key, value) => {
                    setUpdatedOrgStatus(value);
                  }}
                  defaultValue={updatedOrgStatus}
                  options={orgStatusOptions}
                />
              </div>
              <AmazonProfilesTable
                tableData={trellisOrgsData}
                columnsToRender={["name", "profile_type", "status"]}
                isLoading={isTrellisOrgsDataLoading}
                isSelectable={true}
                selectedProfiles={selectedOrgs}
                setSelectedProfiles={setSelectedOrgs}
                selectionKey={"id"}
                hideBulkActionsButton={true}
                defaultPageSize={5}
                hideBottomPagination={true}
              />

              <Flex justifyContent="center" gap="large" className={css({ padding: "20px" })}>
                <Button
                  onPress={() => {
                    reset();
                  }}
                >
                  Cancel
                </Button>

                <Button
                  onPress={() => {
                    const confirmation = confirm(
                      `Are you sure you want to update the status of the ${selectedOrgs?.length} selected organizations to ${updatedOrgStatus?.label}?`,
                    );
                    if (confirmation) {
                      updateStatusHandler.mutate();
                    }
                  }}
                  isDisabled={!updatedOrgStatus?.value || !selectedOrgs?.length || updateStatusHandler.isPending}
                  isPending={updateStatusHandler.isPending}
                >
                  Confirm
                </Button>
              </Flex>
            </Flex>
          }
        />
      </Card>
    </Accordion>
  );
};

export default OrgStatusSection;
